import { WarehouseDocuments } from './warehouse_documents';
import { WAREHOUSE_OPERATIONS } from './warehouse_operations';

const UNITS = {
  en: {
    g: 'g',
    kg: 'kg',
    l: 'l',
    m: 'm',
    ml: 'ml',
    unit: 'pcs',
    cm: 'cm',
  },
  uk: {
    g: 'г',
    kg: 'кг',
    l: 'л',
    m: 'м',
    ml: 'мл',
    unit: 'шт',
    cm: 'см',
  },
};

// Product-form keys — the form lives in the right pane of the warehouses page.
const PRODUCT_FORM = {
  en: {
    Product: 'Product',
    Kit: 'Kit',
    'New Product': 'New Product',
    'Create Product': 'Create Product',
    'Edit Product': 'Edit Product',
    'Update Product': 'Update Product',
    Folder: 'Folder',
    Quantity: 'Quantity',
    Weight: 'Weight',
    Volume: 'Volume',
    Length: 'Length',
    'Business Direction': 'Business Direction',
    Unit: 'Unit',
    'Sale under consignment': 'Sale under consignment',
    'Consignment Hint':
      'The product is taken from a supplier for sale — payment to the supplier happens after the sale.',
    'Sale by package': 'Sale by package',
    'Package Price': 'Package Price',
    'Sale by portion': 'Sale by portion',
    'Portion Size': 'Portion Size',
    'Portion Price': 'Portion Price',
    'Critical Quantity': 'Critical Quantity',
    'Critical Quantity Hint':
      'Below this quantity the product is flagged for reordering.',
    Description: 'Description',
    'Select at least one sale method': 'Select at least one sale method',
    'SKU already taken': 'A product with this SKU already exists',
    'Kit composition': 'Kit composition (recipe)',
    'Kit composition hint':
      'A kit has no stock of its own — selling it writes off the components below. Quantity is per one kit unit, in the component’s own units.',
    Component: 'Component',
    'Add component': 'Add component',
    'Add at least one component': 'Add at least one component',
  },
  uk: {
    Product: 'Товар',
    Kit: 'Комплект',
    'New Product': 'Новий товар',
    'Create Product': 'Створити товар',
    'Edit Product': 'Редагування товару',
    'Update Product': 'Оновити товар',
    Folder: 'Папка',
    Quantity: 'Кількість',
    Weight: 'Вага',
    Volume: "Об'єм",
    Length: 'Довжина',
    'Business Direction': 'Напрямок діяльності',
    Unit: 'Одиниця',
    'Sale under consignment': 'Продаж під реалізацію',
    'Consignment Hint':
      'Товар береться у постачальника під реалізацію — оплата постачальнику після продажу.',
    'Sale by package': 'Продаж упаковкою',
    'Package Price': 'Ціна упаковки',
    'Sale by portion': 'Продаж порціями',
    'Portion Size': 'Розмір порції',
    'Portion Price': 'Ціна порції',
    'Critical Quantity': 'Критична кількість',
    'Critical Quantity Hint':
      'Нижче цієї кількості товар позначається для дозамовлення.',
    Description: 'Опис',
    'Select at least one sale method': 'Оберіть хоча б один спосіб продажу',
    'SKU already taken': 'Товар із таким артикулом вже існує',
    'Kit composition': 'Склад набору (рецепт)',
    'Kit composition hint':
      'У набору немає власного залишку — під час продажу списуються компоненти нижче. Кількість вказується на одну одиницю набору, в одиницях виміру компонента.',
    Component: 'Компонент',
    'Add component': 'Додати компонент',
    'Add at least one component': 'Додайте хоча б один компонент',
  },
};

export const Warehouses = {
  en: {
    ...UNITS.en,
    ...WarehouseDocuments.en,
    ...WAREHOUSE_OPERATIONS.en,
    ...PRODUCT_FORM.en,
    Products: 'Products',
    'All warehouses': 'All warehouses',
    'Aggregated view of all warehouses': 'Aggregated view of all warehouses',
    'No folders': 'No folders',
    'Create folder': 'Create folder',
    'Create subfolder': 'Create subfolder',
    'Edit folder': 'Edit folder',
    'Parent folder': 'Parent folder',
    'Hide from this warehouse': 'Hide from this warehouse',
    'Folders are not deleted, only hidden':
      'Folders are not deleted, only hidden',
    'Select a specific warehouse to hide the folder':
      'Select a specific warehouse to hide the folder',
    'Add product': 'Add product',
    'Reset prototype data': 'Reset prototype data',
    'Reset prototype data confirmation':
      'All locally stored prototype data — warehouses, products, documents and sales — will be deleted and seeded anew. The page will reload.',
    Reset: 'Reset',
    'No stock': 'No stock',
    Folders: 'Folders',
    'Unchecked folders are hidden on this warehouse':
      'Unchecked folders are hidden on this warehouse',
    Operations: 'Operations',
    'Product operations are available on a tablet or desktop':
      'Product operations are available on a tablet or desktop',
    'Coming soon': 'Coming soon',
    Warehouses: 'Warehouses',
    Warehouse: 'Warehouse',
    'Expand all': 'Expand all',
    'Collapse tree': 'Collapse',
    'No warehouses': 'No warehouses',
    'Create Warehouse': 'Create Warehouse',
    'Update Warehouse': 'Update Warehouse',
    'New Warehouse': 'New Warehouse',
    'Edit Warehouse': 'Edit Warehouse',
    'Warehouse saved': 'Warehouse saved',
    'Warehouse not found': 'Warehouse not found',
    'Warehouse holds stock': 'The warehouse holds stock and cannot be archived',
    'Folder holds stock':
      'The folder holds stock on this warehouse and cannot be hidden',
    'Warehouse name already taken at this location':
      'A warehouse with this name already exists at this location',
    'No selection': 'No selection',
    'Select a warehouse or category from the tree on the left to see details.':
      'Select a warehouse or folder from the tree on the left to see details.',
    Location: 'Location',
    'Warehouse Type': 'Warehouse Type',
    'Regular warehouse': 'Regular warehouse',
    'Specialist warehouse': 'Specialist warehouse',
    'Responsible person': 'Responsible person',
    Specialist: 'Specialist',
    Sale: 'Sale method',
    'Packages only': 'Packages only',
    'Sell from warehouse': 'Sell from warehouse',
    'By volume': 'By volume',
    'By portions': 'By portions',
    'By piece': 'By piece',
    'Select at least one sell option': 'Select at least one sell option',
    Penalty: 'Penalty',
    'Penalty Hint':
      'Inventory shortage is deducted from the responsible person’s salary as a % of the purchase-price value. Display only in the prototype.',
    'of purchase-price value': 'of purchase-price value',
    SKU: 'SKU',
    'In Package': 'In Package',
    Search: 'Search',
  },
  uk: {
    ...UNITS.uk,
    ...WarehouseDocuments.uk,
    ...WAREHOUSE_OPERATIONS.uk,
    ...PRODUCT_FORM.uk,
    Products: 'Товари',
    'All warehouses': 'Всі склади',
    'Aggregated view of all warehouses': 'Зведення з усіх складів',
    'No folders': 'Немає папок',
    'Create folder': 'Створити папку',
    'Create subfolder': 'Створити підпапку',
    'Edit folder': 'Редагувати папку',
    'Parent folder': 'Батьківська папка',
    'Hide from this warehouse': 'Приховати з цього складу',
    'Folders are not deleted, only hidden':
      'Папки не видаляються — лише ховаються',
    'Select a specific warehouse to hide the folder':
      'Оберіть конкретний склад, щоб приховати папку',
    'Add product': 'Додати товар',
    'Reset prototype data': 'Скинути дані',
    'Reset prototype data confirmation':
      'Усі локальні дані прототипу — склади, товари, документи та продажі — будуть видалені та створені заново. Сторінка перезавантажиться.',
    Reset: 'Скинути',
    'No stock': 'Немає залишків',
    Folders: 'Папки',
    'Unchecked folders are hidden on this warehouse':
      'Папки зі знятими галочками приховані на цьому складі',
    Operations: 'Операції',
    'Product operations are available on a tablet or desktop':
      'Операції з товарами доступні на планшеті або ПК',
    'Coming soon': 'В розробці',
    Warehouses: 'Склади',
    Warehouse: 'Склад',
    'Expand all': 'Розгорнути все',
    'Collapse tree': 'Згорнути',
    'No warehouses': 'Немає складів',
    'Create Warehouse': 'Створити склад',
    'Update Warehouse': 'Оновити склад',
    'New Warehouse': 'Новий склад',
    'Edit Warehouse': 'Редагування складу',
    'Warehouse saved': 'Склад збережено',
    'Warehouse not found': 'Склад не знайдено',
    'Warehouse holds stock': 'На складі є залишки — архівація неможлива',
    'Folder holds stock':
      'У папці є залишки на цьому складі — приховати не можна',
    'Warehouse name already taken at this location':
      'Склад із такою назвою вже існує на цій локації',
    'No selection': 'Нічого не вибрано',
    'Select a warehouse or category from the tree on the left to see details.':
      'Оберіть склад або папку в дереві зліва, щоб побачити деталі.',
    Location: 'Локація',
    'Warehouse Type': 'Тип складу',
    'Regular warehouse': 'Звичайний склад',
    'Specialist warehouse': 'Склад фахівця',
    'Responsible person': 'Матеріально відповідальний',
    Specialist: 'Фахівець',
    Sale: 'Спосіб продажу',
    'Packages only': 'Товари тільки по упаковкам',
    'Sell from warehouse': 'Продаж зі складу',
    'By volume': "По об'єму (гр, мл)",
    'By portions': 'По порціях',
    'By piece': 'Поштучно',
    'Select at least one sell option': 'Оберіть хоча б один спосіб продажу',
    Penalty: 'Сума штрафу',
    'Penalty Hint':
      'При недостачі на складі під час інвентаризації сума недостачі буде списуватися з зарплати працівника (% від вартості за ціною закупки). У прототипі — лише відображення.',
    'of purchase-price value': 'від вартості за ціною закупки',
    SKU: 'Артикул',
    'In Package': 'В упаковці',
    Search: 'Пошук',
  },
};
